/**
 * Sender proof (PLAN.md 4.18). The server cannot see who signed a private capsule (NIP-59), but it
 * knows which account submitted the wrap and that account's active key at that moment. It tells the
 * recipient that key, encrypted to the key the wrap is addressed to, so a session without the key
 * learns nothing. After unwrapping, the recipient compares it with the inner capsule's signer: equal
 * means the capsule came from the account whose key signed it.
 *
 * Plaintext: {"v": 1, "id": <wrap id>, "submitter": <hex pubkey>}, NIP-44 v2 from a one-time key.
 */
import { generateSecretKey, getPublicKey } from "nostr-tools/pure";
import { decrypt, encrypt, getConversationKey } from "nostr-tools/nip44";

/** `key`: the one-time public key the payload is encrypted from; `content`: the NIP-44 payload. */
export type SenderProof = { key: string; content: string };

export class SenderProofError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SenderProofError";
  }
}

const HEX64 = /^[0-9a-f]{64}$/;

/**
 * One proof per wrap, all from one one-time key (one conversation key per recipient key). Rows
 * without a known submitter key get none.
 */
export function sealSenderProofs(items: { id: string; recipient: string; submitter: string }[]): Map<string, SenderProof> {
  const out = new Map<string, SenderProof>();
  if (items.length === 0) return out;
  const oneTime = generateSecretKey();
  const key = getPublicKey(oneTime);
  const conversation = new Map<string, Uint8Array>();
  for (const item of items) {
    if (!HEX64.test(item.recipient) || !HEX64.test(item.submitter)) continue;
    let ck = conversation.get(item.recipient);
    if (!ck) {
      ck = getConversationKey(oneTime, item.recipient);
      conversation.set(item.recipient, ck);
    }
    out.set(item.id, { key, content: encrypt(JSON.stringify({ v: 1, id: item.id, submitter: item.submitter }), ck) });
  }
  return out;
}

/** The submitter's key from a proof for wrap `wrapId`, opened with the key the wrap is addressed to. */
export function openSenderProof(proof: unknown, wrapId: string, recipientSecretKey: Uint8Array): string {
  const p = proof as Partial<SenderProof> | null | undefined;
  if (!p || typeof p.key !== "string" || !HEX64.test(p.key) || typeof p.content !== "string") throw new SenderProofError("no sender proof");
  let plain: unknown;
  try {
    plain = JSON.parse(decrypt(p.content, getConversationKey(recipientSecretKey, p.key)));
  } catch {
    throw new SenderProofError("the sender proof cannot be opened with this key");
  }
  const v = plain as { v?: unknown; id?: unknown; submitter?: unknown };
  if (v.v !== 1 || v.id !== wrapId || typeof v.submitter !== "string" || !HEX64.test(v.submitter)) {
    throw new SenderProofError("the sender proof is for another capsule or malformed");
  }
  return v.submitter;
}
